import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';

const SupabaseDataContext = createContext(null);

export const useSupabaseData = () => {
    const context = useContext(SupabaseDataContext);
    if (!context) {
        throw new Error('useSupabaseData must be used within a SupabaseDataProvider');
    }
    return context;
};

export const SupabaseDataProvider = ({ children }) => {
    const { toast } = useToast();
    const [students, setStudents] = useState([]);
    const [evaluations, setEvaluations] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStudents = useCallback(async () => {
        setLoading(true);
        const { data, error } = await supabase.from('students').select('*').order('created_at', { ascending: false });
        if (error) {
            toast({ variant: "destructive", title: "Erro ao buscar alunos", description: error.message });
            setStudents([]);
        } else {
            setStudents(data);
        }
        setLoading(false);
    }, [toast]);

    const fetchEvaluations = useCallback(async () => {
        const { data, error } = await supabase.from('evaluations').select('*, students ( student_name )').order('created_at', { ascending: false });
        if (error) {
            toast({ variant: "destructive", title: "Erro ao buscar avaliações", description: error.message });
            setEvaluations([]);
        } else {
            const formattedData = data.map(e => ({...e, studentName: e.students.student_name, totalScore: e.total_score, interpretation: e.result_interpretation }));
            setEvaluations(formattedData);
        }
    }, [toast]);
    
    const fetchEvaluationsByStudent = useCallback(async (studentId) => {
        const { data, error } = await supabase.from('evaluations').select('*').eq('student_id', studentId).order('created_at', { ascending: false });
        if (error) {
            toast({ variant: "destructive", title: "Erro ao buscar avaliações do aluno", description: error.message });
            return [];
        }
        return data.map(e => ({...e, totalScore: e.total_score, interpretation: e.result_interpretation }));
    }, [toast]);

    const fetchClassLogsByStudent = useCallback(async (studentId) => {
        const { data, error } = await supabase.from('class_logs').select('*').eq('student_id', studentId).order('date', { ascending: false });
        if (error) {
            toast({ variant: "destructive", title: "Erro ao buscar relatos de evolução", description: error.message });
            return [];
        }
        return data;
    }, [toast]);

    useEffect(() => {
        fetchStudents();
        fetchEvaluations();
    }, [fetchStudents, fetchEvaluations]);

    const addStudent = async (studentData) => {
        const { data, error } = await supabase.from('students').insert([studentData]).select();
        if (error) {
            toast({ variant: "destructive", title: "Erro ao cadastrar aluno", description: error.message });
            return null;
        }
        toast({ title: "Sucesso!", description: "Aluno cadastrado com sucesso." });
        fetchStudents();
        return data[0];
    };

    const addEvaluation = async (evaluationData) => {
        const { data, error } = await supabase.from('evaluations').insert([evaluationData]).select();
        if (error) {
            toast({ variant: "destructive", title: "Erro ao salvar avaliação", description: error.message });
            return null;
        }
        toast({ title: "Sucesso!", description: "Avaliação salva com sucesso." });
        fetchEvaluations();
        return data[0];
    };

    const addClassLog = async (logData) => {
        const { data, error } = await supabase.from('class_logs').insert([logData]).select();
        if (error) {
            toast({ variant: "destructive", title: "Erro ao salvar relato", description: error.message });
            return null;
        }
        toast({ title: "Sucesso!", description: "Relato de evolução salvo com sucesso." });
        return data[0];
    };

    const deleteStudent = async (studentId) => {
        await supabase.from('evaluations').delete().eq('student_id', studentId);
        await supabase.from('class_logs').delete().eq('student_id', studentId);
        const { error } = await supabase.from('students').delete().eq('id', studentId);
        if (error) {
            toast({ variant: "destructive", title: "Erro ao excluir aluno", description: error.message });
            return false;
        }
        toast({ title: "Sucesso!", description: "Aluno excluído com sucesso." });
        fetchStudents();
        return true;
    };

    const deleteEvaluation = async (evaluationId) => {
        const { error } = await supabase.from('evaluations').delete().eq('id', evaluationId);
        if (error) {
            toast({ variant: "destructive", title: "Erro ao excluir avaliação", description: error.message });
            return false;
        }
        toast({ title: "Sucesso!", description: "Avaliação excluída com sucesso." });
        fetchEvaluations();
        return true;
    };

    const deleteClassLog = async (logId) => {
        const { error } = await supabase.from('class_logs').delete().eq('id', logId);
        if (error) {
            toast({ variant: "destructive", title: "Erro ao excluir relato", description: error.message });
            return false;
        }
        toast({ title: "Sucesso!", description: "Relato de evolução excluído com sucesso." });
        return true;
    };

    const value = {
        students,
        evaluations,
        loading,
        fetchStudents,
        fetchEvaluations,
        fetchEvaluationsByStudent,
        fetchClassLogsByStudent,
        addStudent,
        addEvaluation,
        addClassLog,
        deleteStudent,
        deleteEvaluation,
        deleteClassLog,
    };

    return (
        <SupabaseDataContext.Provider value={value}>
            {children}
        </SupabaseDataContext.Provider>
    );
};